import { Link } from 'react-router-dom';
import { useAdminStats, useAdminUsers, usePendingAccountRequestsCount } from '../../../api/admin';
import { useDonations } from '../../../api/donations';
import { UserStatus, DonationStatus } from '@spi/shared';


export function AdminDashboard() {
  const { data: stats } = useAdminStats();
  const { data: pendingUsers } = useAdminUsers({ status: UserStatus.PENDING, limit: 5 });
  const { data: pendingDonations } = useDonations({ status: DonationStatus.RECEIVED, limit: 5 });
  const { data: pendingApplications } = usePendingAccountRequestsCount();

  return (
    <div className="space-y-8">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="card p-5">
          <p className="text-sm text-spi-slate">Total Users</p>
          <p className="text-2xl font-bold text-spi-green-dark mt-1">{stats?.totalUsers ?? 0}</p>
        </div>
        <div className="card p-5">
          <p className="text-sm text-spi-slate">Pending Approvals</p>
          <p className="text-2xl font-bold text-amber-600 mt-1">{stats?.pendingApprovals ?? 0}</p>
        </div>
        <div className="card p-5">
          <p className="text-sm text-spi-slate">Active Projects</p>
          <p className="text-2xl font-bold text-spi-green-dark mt-1">{stats?.activeProjects ?? 0}</p>
        </div>
        <div className="card p-5">
          <p className="text-sm text-spi-slate">Pending Verifications</p>
          <p className="text-2xl font-bold text-amber-600 mt-1">{stats?.pendingVerifications ?? 0}</p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="card p-5">
          <p className="text-sm text-spi-slate">Total Donated (kg)</p>
          <p className="text-2xl font-bold text-spi-green-dark mt-1">{(stats?.totalDonatedKg ?? 0).toFixed(1)}</p>
        </div>
        <div className="card p-5">
          <p className="text-sm text-spi-slate">Total Value (USD)</p>
          <p className="text-2xl font-bold text-spi-green-dark mt-1">${(stats?.totalDonatedValueUsd ?? 0).toLocaleString()}</p>
        </div>
        <div className="card p-5">
          <p className="text-sm text-spi-slate">Total Projects</p>
          <p className="text-2xl font-bold text-spi-green-dark mt-1">{stats?.totalProjects ?? 0}</p>
        </div>
        <div className="card p-5">
          <p className="text-sm text-spi-slate">Open Asks</p>
          <p className="text-2xl font-bold text-spi-green-dark mt-1">{stats?.openAsks ?? 0}</p>
        </div>
      </div>

      {/* Action Required */}
      {((pendingApplications ?? 0) > 0 || (pendingUsers?.data.length ?? 0) > 0 || (pendingDonations?.data.length ?? 0) > 0) ? (
        <div className="rounded-lg border-l-4 border-amber-500 bg-amber-50 p-5 space-y-5">
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-semibold text-amber-800">Action Required</h3>
            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-amber-500 text-white text-xs font-bold">
              {(pendingApplications ?? 0) + (pendingUsers?.data.length ?? 0) + (pendingDonations?.data.length ?? 0)}
            </span>
          </div>

          {/* Pending Account Applications */}
          {(pendingApplications ?? 0) > 0 && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium text-amber-700">Account Applications ({pendingApplications})</p>
                <Link to="/marketplace/admin?tab=applications" className="text-xs text-amber-600 font-semibold hover:underline">
                  Review all &rarr;
                </Link>
              </div>
            </div>
          )}

          {/* Pending User Approvals */}
          {pendingUsers?.data.length ? (
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium text-amber-700">User Approvals ({pendingUsers.data.length})</p>
                <Link to="/marketplace/admin" className="text-xs text-amber-600 font-semibold hover:underline">
                  Manage all &rarr;
                </Link>
              </div>
              <div className="space-y-2">
                {pendingUsers.data.map((u) => (
                  <Link key={u.id} to={`/marketplace/admin?tab=users&userId=${u.id}`} className="flex items-center justify-between bg-white rounded-md p-3 shadow-sm hover:shadow-md transition-shadow">
                    <div>
                      <p className="font-medium text-spi-charcoal text-sm">{u.firstName} {u.lastName}</p>
                      <p className="text-xs text-spi-slate">{u.email} &middot; {u.organization?.name} &middot; {u.role}</p>
                    </div>
                    <span className="text-xs font-semibold text-amber-600 whitespace-nowrap">Review &rarr;</span>
                  </Link>
                ))}
              </div>
            </div>
          ) : null}

          {/* Pending Donation Verifications */}
          {pendingDonations?.data.length ? (
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium text-amber-700">Donation Verifications ({pendingDonations.data.length})</p>
                <Link to="/marketplace/admin?tab=transactions" className="text-xs text-amber-600 font-semibold hover:underline">
                  Manage all &rarr;
                </Link>
              </div>
              <div className="space-y-2">
                {pendingDonations.data.map((donation) => (
                  <Link key={donation.id} to={`/marketplace/donations/${donation.id}`} className="flex items-center justify-between bg-white rounded-md p-3 shadow-sm hover:shadow-md transition-shadow">
                    <div>
                      <p className="font-medium text-spi-charcoal text-sm">{donation.projectTitle}</p>
                      <p className="text-xs text-spi-slate">{donation.donorOrganization} &rarr; {donation.recipientOrganization} &middot; {donation.askCrop}</p>
                    </div>
                    <span className="text-xs font-semibold text-amber-600 whitespace-nowrap">Verify &rarr;</span>
                  </Link>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      ) : (stats || pendingUsers || pendingDonations || pendingApplications !== undefined) ? (
        <div className="rounded-lg border-l-4 border-spi-green bg-green-50 p-4 flex items-center gap-3">
          <svg className="w-5 h-5 text-spi-green flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          <p className="text-sm font-medium text-green-800">All caught up! No items need your attention right now.</p>
        </div>
      ) : null}

    </div>
  );
}
